import React, { useEffect, useState } from "react"
import {
  Modal, Form, Select, Checkbox, Button, Spin, Popconfirm,
  message, Tag, Popover, Row, Col, Input, InputNumber, Tooltip, Image, Space
} from "antd"
import TextArea from "antd/es/input/TextArea"
import {
  EditOutlined,
  TagOutlined,
  InboxOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
  BarcodeOutlined,
  DollarOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  DeploymentUnitOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { useRegister } from "../../contexts/RegisterContext"
import { useCompany } from "../../contexts/CompanyContext"
import { useSecureRequest } from "../../hooks/useSecureRequest"
import { useStore } from "../../contexts/StoreContext"
import { mutate } from "swr"
import { TStorageItemRS, TElementPrices } from "../../models/storageModel"
import { TElementModuleOptions } from "../../models/salesModel"
import { formatInputNumber, parseInputNumber } from "../../utils"

type TInventoryEntry = { id: number; quantity: number; reserved: number; uid: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCheckboxOptions(item: any) {
  const options: string[] = []
  if (item?.sell_item) options.push("SALE")
  if (item?.purchase_item) options.push("PURCHASE")
  return options
}

const checkboxOptions = [
  { label: "Habilitar compra", value: "PURCHASE" },
  { label: "Habilitar venta", value: "SALE" },
]

// ─── Estilos ──────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#8c8c8c",
  marginBottom: 2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
}
const valueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#262626",
  fontWeight: 500,
}
const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "8px 0",
  borderBottom: "1px solid #f0f0f0",
}

const InfoRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) => (
  <div style={rowStyle}>
    <div style={{ paddingTop: 2, color: "#bfbfbf", flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{children}</div>
    </div>
  </div>
)

// ─── Vista lectura ────────────────────────────────────────────────────────────

interface ViewProductProps {
  item: TStorageItemRS
  onEdit: () => void
  canEdit: boolean
  register: any
  store: any
}

const ViewProduct: React.FC<ViewProductProps> = ({ item, onEdit, canEdit, register, store }) => {
  const getCategoryName = (key: string) =>
    register.categoryOptions.find((o: any) => o.key === key)?.string_value || key || "—"

  const getTypeName = (key: string) =>
    register.itemTypes.find((o: any) => o.key === key)?.string_value || key || "—"

  const getSellPrice = () =>
    item.prices?.find((p: any) => p.type === "SELL")?.value ?? item.sell_price

  const getPurchasePrice = () =>
    item.prices?.find((p: any) => p.type === "PURCHASE")?.value ?? item.purchase_price

  const formatCOP = (v?: number) =>
    v === undefined ? "—" : `$${v.toLocaleString("es-CO")} COP`

  const renderStock = (inventory: TInventoryEntry[], register_type: string) => {
    if (register_type === "SERVICE") return <Tag color="default">No aplica</Tag>

    const total = (!inventory || inventory.length === 0)
      ? 0
      : inventory.reduce((s, e) => s + e.quantity, 0)

    let warningIcon = null
    if (total === 0) {
      warningIcon = (
        <Tooltip title="No hay unidades disponibles. Puedes agregarlas desde el módulo de Storage haciendo un movimiento de entrada.">
          <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 6 }} />
        </Tooltip>
      )
    } else if (total < 0) {
      warningIcon = (
        <Tooltip title="Se hicieron movimientos sin tener unidades. Debes reabastecer con un movimiento desde el módulo de Storage.">
          <ExclamationCircleOutlined style={{ color: '#f5222d', marginLeft: 6 }} />
        </Tooltip>
      )
    }

    if (!inventory || inventory.length === 0 || inventory.length === 1) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{total}</span>
          {warningIcon}
        </div>
      )
    }

    const content = (
      <div style={{ minWidth: 180 }}>
        {inventory.map((entry) => {
          const name = store.list.find((s: any) => s.uid === entry.uid)?.name || entry.uid
          return (
            <div key={entry.uid} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
              <span>{name}</span>
              <strong>{entry.quantity}</strong>
            </div>
          )
        })}
      </div>
    )
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Popover title="Existencias por almacén" content={content} trigger="hover">
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #1890ff", color: "#1890ff" }}>{total}</span>
        </Popover>
        {warningIcon}
      </div>
    )
  }

  const productImages = (item.images && item.images.length > 0)
    ? item.images
    : (item.image ? [{ file_name: item.name || "Foto", file_url: item.image, is_primary: true, display_order: 0, storage_path: "" }] : [])
  const primaryImage = productImages.find((img: any) => img.is_primary) || productImages[0]
  const [activeImgIndex, setActiveImgIndex] = useState(0)
  const activeImage = productImages[activeImgIndex] || primaryImage

  return (
    <div style={{ padding: "0 4px 8px" }}>
      {/* Encabezado con Nombre e ID */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: "#262626" }}>{item.name || "—"}</div>
        {item.custom_id && (
          <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 2 }}>ID: {item.custom_id}</div>
        )}
      </div>

      {/* Galería de Imágenes en Vista Detalle */}
      {productImages.length > 0 ? (
        <div
          style={{
            background: "#fafafa",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
              borderRadius: 6,
              overflow: "hidden",
              marginBottom: productImages.length > 1 ? 10 : 0,
            }}
          >
            <Image
              src={activeImage?.file_url}
              alt={item.name}
              style={{
                maxHeight: 200,
                width: "100%",
                objectFit: "contain",
                padding: 6,
              }}
              preview={{
                mask: (
                  <Space>
                    <EyeOutlined /> Ampliar imagen
                  </Space>
                ),
              }}
              fallback="assets/images/no-data-image.svg"
            />
          </div>

          {/* Tiras de miniaturas si hay más de 1 imagen */}
          {productImages.length > 1 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {productImages.map((img: any, idx: number) => (
                <div
                  key={img.uid || img.id || idx}
                  onClick={() => setActiveImgIndex(idx)}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 4,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: activeImgIndex === idx ? "2px solid #1890ff" : "1px solid #d9d9d9",
                    flexShrink: 0,
                    background: "#ffffff",
                    position: "relative",
                  }}
                >
                  <img
                    src={img.file_url}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {img.is_primary && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        background: "#faad14",
                        color: "#fff",
                        fontSize: 8,
                        padding: "0 2px",
                        lineHeight: "10px",
                        borderRadius: "2px 0 0 0",
                      }}
                    >
                      ★
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div style={{ background: "#fafafa", borderRadius: 8, padding: "4px 16px", marginBottom: 20 }}>
        <InfoRow icon={<AppstoreOutlined />} label="Tipo">
          <Tag color="blue">{getTypeName(item.register_type)}</Tag>
        </InfoRow>

        <InfoRow icon={<TagOutlined />} label="Categoría">
          {getCategoryName(item.category)}
        </InfoRow>

        {item.description && (
          <InfoRow icon={<FileTextOutlined />} label="Descripción">
            <span style={{ ...valueStyle, fontWeight: 400 }}>{item.description}</span>
          </InfoRow>
        )}

        {item.register_type !== "SERVICE" && (
          <InfoRow icon={<InboxOutlined />} label="Existencias">
            {renderStock((item.inventory || []) as TInventoryEntry[], item.register_type)}
          </InfoRow>
        )}

        {item.location && (
          <InfoRow icon={<EnvironmentOutlined />} label="Ubicación">
            {item.location}
          </InfoRow>
        )}

        {item.barcode && (
          <InfoRow icon={<BarcodeOutlined />} label="Código de barras">
            {item.barcode}
          </InfoRow>
        )}

        {item.sell_item && (
          <InfoRow icon={<DollarOutlined />} label="Precio de venta">
            {formatCOP(getSellPrice())}
          </InfoRow>
        )}

        {item.purchase_item && (
          <InfoRow icon={<DollarOutlined />} label="Precio de compra">
            {formatCOP(getPurchasePrice())}
          </InfoRow>
        )}

        {item.is_traceable && (
          <InfoRow icon={<DeploymentUnitOutlined style={{ color: "#1d39c4" }} />} label="Trazabilidad">
            <Tag color="blue">Trazabilidad Individual Habilitada</Tag>
          </InfoRow>
        )}

        {item.other_values?.filter((v: any) => v.is_visible && v.field_name && v.field_value).map((v: any) => (
          <InfoRow key={v.id} icon={<FileTextOutlined />} label={v.field_name}>
            {v.field_value}
          </InfoRow>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {canEdit && (
          <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
            Editar
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Formulario de edición simplificado ──────────────────────────────────────
// En ventas, para la edición se muestra un formulario de campos clave
// (los subformularios completos son específicos de ryo-storage-front y dependen
// de sus contextos internos; aquí se editan los campos principales)

const EditProductForm: React.FC<{ form: any; item: TStorageItemRS; register: any }> = ({ form, item, register }) => {
  const { Option } = Select
  const checkedList: string[] = Form.useWatch("registerOptions", form) || []

  const categories = register.categoryOptions.filter((el: any) => {
    if (el.key === item.category) return true
    return el.show === true
  })

  const presentations = register.displayOptions.filter((el: any) => {
    if (el.key === item.display) return true
    return el.show === true
  })

  const ums = register.umOptions.filter((el: any) => {
    if (el.key === item.measure_unit) return true
    return el.show === true
  })

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col xs={24} sm={{ span: 10, offset: 2 }}>
          <Form.Item label="Tipo de registro" name="register_type">
            <Select disabled>
              {register.itemTypes.map((el: any) => (
                <Option key={el.id} value={el.key}>{el.string_value}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {item.register_type === "PRODUCT" && (
          <Col xs={24} sm={{ span: 10 }}>
            <Form.Item label="Opciones de registro" name="registerOptions"
              rules={[{ required: true, message: "Por favor selecciona una opción" }]}>
              <Checkbox.Group options={checkboxOptions} value={checkedList} />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={[10, 10]}>
        <Col xs={24} sm={{ span: 10, offset: 2 }}>
          <Form.Item label="Nombre" name="name"
            rules={[{ required: true, message: "Por favor ingresa el nombre" }]}>
            <Input placeholder="Nombre del producto" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={{ span: 10 }}>
          <Form.Item label="Identificador" name="custom_id">
            <Input placeholder="ID personalizado"
              disabled={!!item.custom_id && item.custom_id.length > 0} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={{ span: 20, offset: 2 }}>
          <Form.Item label="Descripción" name="description"
            rules={[{ required: true, message: "Por favor ingresa la descripción" }]}>
            <TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={{ span: 10, offset: 2 }}>
          <Form.Item label="Categoría" name="category">
            <Select placeholder="Selecciona categoría">
              {categories.map((v: any) => <Option key={v.key} value={v.key}>{v.string_value}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        {item.register_type !== "SERVICE" && (
          <Col xs={24} sm={{ span: 10 }}>
            <Form.Item label="Presentación" name="display">
              <Select placeholder="Selecciona presentación">
                {presentations.map((v: any) => <Option key={v.key} value={v.key}>{v.string_value}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        )}
        {item.register_type !== "SERVICE" && (
          <Col xs={24} sm={{ span: 10, offset: 2 }}>
            <Form.Item label="Unidad de medida" name="measure_unit">
              <Select placeholder="Selecciona unidad">
                {ums.map((v: any) => <Option key={v.key} value={v.key}>{v.string_value}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        )}
        {item.register_type !== "SERVICE" && (
          <Col xs={24} sm={{ span: 10 }}>
            <Form.Item label="Ubicación" name="location">
              <Input placeholder="Ubicación - Estante" />
            </Form.Item>
          </Col>
        )}
      </Row>

      {/* Precios de venta */}
      {checkedList.includes("SALE") && (
        <>
          <h5 style={{ marginLeft: "8.3333%", marginBottom: "0.5em", fontWeight: 500, fontSize: "1rem", marginTop: "0.5rem" }}>
            Datos de Venta
          </h5>
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={{ span: 10, offset: 2 }}>
              <Form.Item label="Precio base" name="sell_price"
                rules={[{ required: true, message: "Por favor ingresa el valor" }]}>
                <InputNumber prefix="$" style={{ width: "100%" }}
                  formatter={formatInputNumber} parser={parseInputNumber} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={{ span: 10 }}>
              <Form.Item label="Descuento (%)" name="sell_discount"
                rules={[{ required: true, message: "Por favor ingresa un valor" }]}>
                <InputNumber suffix="%" min={0} max={100}
                  formatter={formatInputNumber} parser={parseInputNumber} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* Precios de compra */}
      {checkedList.includes("PURCHASE") && (
        <>
          <h5 style={{ marginLeft: "8.3333%", marginBottom: "0.5em", fontWeight: 500, fontSize: "1rem", marginTop: "0.5rem" }}>
            Datos de Compra
          </h5>
          <Row gutter={[10, 10]}>
            <Col xs={24} sm={{ span: 10, offset: 2 }}>
              <Form.Item label="Precio base" name="purchase_price"
                rules={[{ required: true, message: "Por favor ingresa el valor" }]}>
                <InputNumber prefix="$" style={{ width: "100%" }}
                  formatter={formatInputNumber} parser={parseInputNumber} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={{ span: 10 }}>
              <Form.Item label="Descuento (%)" name="purchase_discount"
                rules={[{ required: true, message: "Por favor ingresa un valor" }]}>
                <InputNumber suffix="%" min={0} max={100}
                  formatter={formatInputNumber} parser={parseInputNumber} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface ViewItemModalProps {
  visible: boolean
  item: TStorageItemRS | null
  onCancel: () => void
  onSuccess: () => void
  canEdit?: boolean
}

export const ViewItemModal: React.FC<ViewItemModalProps> = ({
  visible,
  item: initialItem,
  onCancel,
  onSuccess,
  canEdit = true,
}) => {
  const { register } = useRegister()
  const { company } = useCompany()
  const { store } = useStore()
  const { securePost } = useSecureRequest()

  const [form] = Form.useForm()
  const [isFormMode, setIsFormMode] = useState(false)
  const [spinning, setSpinning] = useState(false)
  const [item, setItem] = useState<TStorageItemRS | null>(null)

  // Helpers ─────────────────────────────────────────────────────────────────

  const getFormTaxes = (values: any, type: "sell" | "purchase") => {
    const arrayTaxes: Partial<TElementModuleOptions>[] = []
    const typeConfig =
      type === "sell"
        ? { subType: "SELL_TAX", activeKey: "sell_taxes_active_", searchKey: "sell_taxes_" }
        : { subType: "PURCHASE_TAX", activeKey: "purchase_taxes_active_", searchKey: "purchase_taxes_" }
    for (const key in values) {
      if (!values[key]) delete values[key]
      if (key.includes("_") && values[key]) {
        if (key.includes(typeConfig.searchKey)) {
          const selectedTax = register.taxesOptions.find((el: any) => el.key === values[key])
          if (selectedTax) {
            const taxItem: Partial<TElementModuleOptions> = {
              sub_type: typeConfig.subType,
              option_id: selectedTax.id,
              category: "NATIONAL",
              is_active: values[`${typeConfig.activeKey}${key.split("_").pop()}`] ?? true,
            }
            taxItem.id = Number(key.split("_").pop()!)
            taxItem.element_id = item?.id
            arrayTaxes.push(taxItem)
          }
        }
        delete values[key]
      }
    }
    return arrayTaxes
  }

  const getPrices = (values: any, registerOptions: string[]): Partial<TElementPrices>[] => {
    const basePrice = { element_id: item?.id || 0, is_default: true, is_active: true, is_visible: true }
    const prices: Partial<TElementPrices>[] = []
    if (registerOptions.includes("SALE") || values.register_type === "SERVICE") {
      prices.push({ ...basePrice, id: item?.prices?.find((p: any) => p.type === "SELL")?.id ?? -1, type: "SELL", value: values.sell_price, description: "Precio de venta" })
    }
    if (registerOptions.includes("PURCHASE")) {
      prices.push({ ...basePrice, id: item?.prices?.find((p: any) => p.type === "PURCHASE")?.id ?? -1, type: "PURCHASE", value: values.purchase_price, description: "Precio de compra" })
    }
    if (values.sell_discount >= 0) {
      prices.push({ ...basePrice, id: item?.prices?.find((p: any) => p.type === "SELL_DISCOUNT")?.id ?? -1, type: "SELL_DISCOUNT", value: values.sell_discount, description: "Descuento de venta" })
    }
    if (values.purchase_discount >= 0) {
      prices.push({ ...basePrice, id: item?.prices?.find((p: any) => p.type === "PURCHASE_DISCOUNT")?.id ?? -1, type: "PURCHASE_DISCOUNT", value: values.purchase_discount, description: "Descuento de compra" })
    }
    return prices
  }

  const getModuleElementOptions = (values: any): Partial<TElementModuleOptions>[] => {
    const moduleOptions: Partial<TElementModuleOptions>[] = []
    const idCategoryOption = register.categoryOptions.find((el: any) => el.key === values.category)?.id
    const idDisplayOption = register.displayOptions.find((el: any) => el.key === values.display)?.id
    const idItemTypeOption = register.itemTypes.find((el: any) => el.key === values.register_type)?.id
    const idUmOption = register.umOptions.find((el: any) => el.key === values.measure_unit || (values.measure_unit === "UN" && el.key === "UND"))?.id
    if (idCategoryOption) moduleOptions.push({ id: item?.module_options?.find((el: any) => el.sub_type === "CATEGORY_ELEMENT")?.id ?? -1, element_id: item?.id, option_id: idCategoryOption, is_active: true, sub_type: "CATEGORY_ELEMENT", category: "NATIONAL" })
    if (idDisplayOption) moduleOptions.push({ id: item?.module_options?.find((el: any) => el.sub_type === "DISPLAY_ELEMENT")?.id ?? -1, element_id: item?.id, option_id: idDisplayOption, is_active: true, sub_type: "DISPLAY_ELEMENT", category: "NATIONAL" })
    if (idItemTypeOption) moduleOptions.push({ id: item?.module_options?.find((el: any) => el.sub_type === "TYPE_ELEMENT")?.id ?? -1, element_id: item?.id, option_id: idItemTypeOption, is_active: true, sub_type: "TYPE_ELEMENT", category: "NATIONAL" })
    if (idUmOption) moduleOptions.push({ id: item?.module_options?.find((el: any) => el.sub_type === "UM_ELEMENT")?.id ?? -1, element_id: item?.id, option_id: idUmOption, is_active: true, sub_type: "UM_ELEMENT", category: "NATIONAL" })
    return moduleOptions
  }

  const toDataForm = (data: any) => {
    if (!data) return {}
    const form: any = {
      register_type: data.register_type,
      name: data.name,
      custom_id: data.custom_id,
      description: data.description,
      category: data.category,
      display: data.display,
      measure_unit: data.measure_unit,
      location: data.location,
      sku: data.sku,
      sell_price: data.sell_price ?? data.prices?.find((p: any) => p.type === "SELL")?.value,
      purchase_price: data.purchase_price ?? data.prices?.find((p: any) => p.type === "PURCHASE")?.value,
      sell_discount: data.sell_discount ?? 0,
      purchase_discount: data.purchase_discount ?? 0,
      registerOptions: getCheckboxOptions(data),
    }
    return form
  }

  // Sincronizar al abrir ────────────────────────────────────────────────────
  useEffect(() => {
    const loadFullItem = async () => {
      if (!initialItem?.uid) return
      setSpinning(true)
      try {
        const res = await securePost("/inventory/item/get-item", {
          uidItem: initialItem.uid,
          company: company.current?.uid,
          module: "STORAGE_ITEM_LIST",
        })
        setSpinning(false)
        if (res.status === 200) {
          setItem(res.data)
          form.setFieldsValue(toDataForm(res.data))
        } else {
          message.error(res.message || "Error al cargar el detalle del producto")
          onCancel()
        }
      } catch (err) {
        console.error(err)
        setSpinning(false)
        message.error("Error de red al cargar el producto")
        onCancel()
      }
    }

    if (visible && initialItem) {
      setItem(initialItem)
      form.setFieldsValue(toDataForm(initialItem))
      setIsFormMode(false)
      loadFullItem()
    }
    if (!visible) {
      setIsFormMode(false)
    }
  }, [visible, initialItem?.uid])

  // Guardar ─────────────────────────────────────────────────────────────────
  const onFinishForm = async (values: any) => {
    if (!item) return
    const registerOptions: string[] = values.registerOptions || []
    const sellTaxes = getFormTaxes({ ...values }, "sell")
    const purchaseTaxes = getFormTaxes({ ...values }, "purchase")
    const prices = getPrices({ ...values }, registerOptions)
    const moduleElementOptions = getModuleElementOptions({ ...values })
    const module_options = [...moduleElementOptions, ...sellTaxes, ...purchaseTaxes]

    const storageElement: Partial<TStorageItemRS> = {
      id: item.id,
      company_id: company.current?.id,
      custom_id: values.custom_id,
      description: values.description,
      sell_discount: values.sell_discount,
      purchase_discount: values.purchase_discount,
      name: values.name,
      purchase_item: registerOptions.includes("PURCHASE"),
      sell_item: registerOptions.includes("SALE"),
      sku: values.sku,
      location: values.location,
      is_active: true,
      image_mini: item.image_mini || "",
      image: item.image || "",
      company_uid: company.current?.uid || "",
      display: values.display,
      sell_price: values.sell_price,
      purchase_price: values.purchase_price,
      quantity: 0,
      register_type: values.register_type,
      measure_unit: values.measure_unit,
      category: values.category,
      module_options,
      prices,
      other_values: item.other_values || [],
    }

    if (values.register_type === "SERVICE") {
      storageElement.display = "N/A"
      storageElement.location = "N/A"
      storageElement.measure_unit = "UN"
      storageElement.sell_item = true
    }

    setSpinning(true)
    const itemResponse = await securePost("/inventory/item/update-item", {
      storageElement,
      company: company.current?.uid,
      uidCompany: company.current?.uid,
      uidItem: item.uid,
      module: "EDIT_STORAGE_ITEM",
    })
    setSpinning(false)

    if (itemResponse.status === 200) {
      mutate((key) => Array.isArray(key) && key[0] === "/inventory/item/get-items", undefined, { revalidate: true })
      message.success("Producto actualizado correctamente")
      onSuccess()
    } else {
      message.error(itemResponse.message || "Error al actualizar el producto")
    }
  }

  const displayItem = item ?? initialItem

  const modalTitle = isFormMode
    ? `Editar · ${displayItem?.name || ""}`
    : displayItem?.name || "Detalle del producto"

  const formFooter = [
    <Button key="cancel" onClick={() => setIsFormMode(false)}>Volver</Button>,
    <Popconfirm key="confirm" title="¿Confirmar actualización?" onConfirm={() => form.submit()} okText="Sí" cancelText="No">
      <Button type="primary" loading={spinning}>Actualizar</Button>
    </Popconfirm>,
  ]

  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{modalTitle}</div>}
      open={visible}
      onCancel={onCancel}
      footer={isFormMode ? formFooter : null}
      maskClosable={false}
      destroyOnHidden
      width={820}
    >
      <Spin spinning={spinning}>
        {!isFormMode && displayItem && (
          <ViewProduct
            item={displayItem}
            onEdit={() => setIsFormMode(true)}
            canEdit={canEdit}
            register={register}
            store={store}
          />
        )}

        {isFormMode && displayItem && (
          <Form
            form={form}
            layout="vertical"
            name="edit-item-modal-sales"
            scrollToFirstError={{ block: "end", focus: true }}
            onFinish={onFinishForm}
          >
            <EditProductForm form={form} item={displayItem} register={register} />
          </Form>
        )}
      </Spin>
    </Modal>
  )
}

export default ViewItemModal
